import styled, { createGlobalStyle } from "styled-components";

import { getBreakpoint, getSemanticColor, getShadow, getSpacing, getBorderWidth } from "@braze/beacon-styling";

import { StyledBrazeMarkdown } from "@components/BrazeMarkdown/BrazeMarkdown.style";
import { StyledTableContainer } from "@components/BrazeMarkdown/TableComponent/TableComponent.style";

import { FIXED_MODAL_TOTAL_WIDTH } from "@canvas/CanvasGrid/components/ModalEditStep/ModalEditStep.style";

import type { FullscreenMode } from "./constants";

export const widthTransitionDurationMs = 250;

const composerNavigationBarHeight = 54;
const composerFooterBarHeight = 64;

export const StyledAgenticChatInterface = styled.div.attrs<{ width: number; zIndex: number }>((props) => ({
  style: {
    width: props.width,
    zIndex: props.zIndex,
  },
}))<{ width: number; zIndex: number }>`
  position: fixed;
  overflow: hidden;

  --agentic-chat-interface-vertical-margin: ${getSpacing("lg")};
  --agentic-chat-interface-horizontal-margin: ${getSpacing("xl")};
  --agentic-chat-interface-right-offset: 0px;

  top: var(--top-nav-height, 91px);
  bottom: 0;
  right: 0;
  border-radius: 0;

  &[data-is-setting-width="true"] {
    transition: width ${widthTransitionDurationMs}ms ease-in-out;
  }

  // When expanded past the inline minimum width, show shadows (overlay-like behavior).
  &[data-expanded="true"] {
    box-shadow: ${getShadow(4)};
  }

  &[data-fullscreen-mode] {
    top: 0;
  }
  // Override table styles in the braze markdown component when the chat
  // interface is not maximized:
  // - Change table cell padding
  // - Wrap content in tables with 1-2 columns, but keep the first
  //   column (e.g. property labels) from wrapping so short labels
  //   like "Status" or "Filters" never break across lines.
  &[data-maximized="false"] {
    ${StyledBrazeMarkdown} ${StyledTableContainer} table {
      th,
      td {
        padding-left: ${getSpacing("md")};
        padding-right: ${getSpacing("md")};
        white-space: normal;
      }

      th:first-child,
      td:first-child {
        white-space: nowrap;
      }

      &:has(tr > td:nth-child(3)) {
        th,
        td {
          white-space: nowrap;
        }
      }
    }
  }

  background:
    radial-gradient(60% 30% at 30% 105%, rgba(86, 112, 255, 0.4) 10%, rgba(255, 255, 255, 0) 100%),
    radial-gradient(60% 30% at 78% 94%, rgba(255, 164, 251, 0.4) 10%, rgba(255, 255, 255, 0) 100%), #fff;

  border-left: ${getBorderWidth("thin")} solid ${getSemanticColor("neutral-border-decorative")};
  box-shadow: none;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;

  // See useAgentFullscreenMode for expected fullscreen modes.
  &[data-fullscreen-mode="${"canvas_modal_edit" satisfies FullscreenMode}"] {
    --agentic-chat-interface-right-offset: calc((100vw - ${FIXED_MODAL_TOTAL_WIDTH}px) / 2);
  }

  &[data-fullscreen-mode="${"canvas_version_history" satisfies FullscreenMode}"],
  &[data-fullscreen-mode="${"banner_composer" satisfies FullscreenMode}"],
  &[data-fullscreen-mode="${"content_block_composer" satisfies FullscreenMode}"],
  &[data-fullscreen-mode="${"subscription_page_composer" satisfies FullscreenMode}"],
  &[data-fullscreen-mode="${"email_footer_composer" satisfies FullscreenMode}"] {
    --top-nav-height: ${composerNavigationBarHeight}px;
    --bottom-nav-height: 0px;
  }

  &[data-fullscreen-mode="${"iam_composer" satisfies FullscreenMode}"],
  &[data-fullscreen-mode="${"line_composer" satisfies FullscreenMode}"],
  &[data-fullscreen-mode="${"line_editor" satisfies FullscreenMode}"],
  &[data-fullscreen-mode="${"landing_pages_composer" satisfies FullscreenMode}"],
  &[data-fullscreen-mode="${"email_preference_center_composer" satisfies FullscreenMode}"] {
    --top-nav-height: 0px;
    --bottom-nav-height: ${composerFooterBarHeight}px;
  }

  &[data-fullscreen-mode="${"canvas_fullscreen" satisfies FullscreenMode}"],
  &[data-fullscreen-mode="${"classic_editor_expanded" satisfies FullscreenMode}"],
  &[data-fullscreen-mode="${"media_library_modal" satisfies FullscreenMode}"] {
    --top-nav-height: 0px;
    --bottom-nav-height: 0px;
  }

  &[data-fullscreen-mode="${"braze_modal" satisfies FullscreenMode}"] {
    --top-nav-height: 0px;
    --bottom-nav-height: 0px;
    --agentic-chat-interface-vertical-margin: 0px;
  }

  // Mobile responsive mode.
  // Note: width uses !important to override inline styles set via .attrs() - this is unavoidable
  @media (max-width: ${getBreakpoint("xl")}px) {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100vw !important;
    height: auto;
    border-radius: 0;
    --agentic-chat-interface-vertical-margin: 0px;
    --agentic-chat-interface-horizontal-margin: 0px;
    --agentic-chat-interface-right-offset: 0px;

    // When no system banner is shown, reduce top offset
    body.react-nav-env-banner--other & {
      top: 52px;
    }

    // Fullscreen modals: take entire screen
    &[data-fullscreen-mode]:not([data-fullscreen-mode=""]) {
      top: 0;
      height: 100vh;
      --top-nav-height: 0px;
      --bottom-nav-height: 0px;
    }
  }
`;

export const StyledHeaderContainer = styled.div`
  background: var(--Linear-Teal, linear-gradient(90deg, #c4f0f5 2.78%, #e7f9fb 72.69%));
  opacity: 0.8;
  display: flex;
  justify-content: space-between;
  padding: ${getSpacing("lg")};
  border-radius: 6px 6px 0 0;

  // Remove rounded corners on mobile (full-page mode).
  @media (max-width: ${getBreakpoint("xl")}px) {
    border-radius: 0;
  }
`;

export const StyledHeaderButtonsContainer = styled.div`
  display: flex;

  // Hide resize button on mobile (full-page mode).
  @media (max-width: ${getBreakpoint("xl")}px) {
    .resize-btn {
      display: none;
    }
  }
`;

export const StyledBodyContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
`;

export const StyledResizeHandle = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 8px;
  cursor: ew-resize;
  /* larger hit target but visual minimalism */
  background: transparent;
  z-index: 1;

  // Hide resize handle on mobile.
  @media (max-width: ${getBreakpoint("xl")}px) {
    display: none;
  }
`;

/**
 * Grapes full-screen editors have no page content to overlay.
 * They must reserve Operator's actual width, not its fixed minimum width.
 */
export const InjectedInlineAgentGlobalCSS = createGlobalStyle`
  /* Email composer portal - fullscreen so no side nav offset */
  #email-message-composer-portal {
    width: calc(100vw - var(--operator-inline-width, 0px)) !important;
    min-width: calc(100vw - var(--operator-inline-width, 0px)) !important;
  }

  /* Banner HTML composer portal - full-screen modal editor */
  #banner-html-composer-portal {
    width: calc(100vw - var(--operator-inline-width, 0px)) !important;
    min-width: calc(100vw - var(--operator-inline-width, 0px)) !important;
  }

  /* GrapesJS-based composers (Banner, IAM, Line, Landing Pages) */
  .db-drag-and-drop-composer.modal {
    /* Do not set min-width here. The composer already has its own min-width. */
    width: calc(100vw - var(--operator-overlay-width, 0px)) !important;
  }

  .editor-domain-iframe {
    width: calc(100vw - var(--operator-overlay-width, 0px)) !important;
  }

  /* Classic email editor expanded full-screen (replace_cke_with_tiptap off) */
  #html-editor-container {
    width: calc(100vw - var(--operator-inline-width, 0px)) !important;
    max-width: calc(100vw - var(--operator-inline-width, 0px)) !important;
  }
`;
